import type { FC } from "react";
import { useQuery } from "@apollo/client";
import { Link as RouterLink } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import MuiLink from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { ErrorMessage } from "@/shared/components";
import {
  ContractsListQuery,
  type ContractsListRow,
} from "@/admin/entities/contracts";
import type { ContractsListQueryType } from "@/types/graphql/admin.generated";
import { PagePaths } from "@/admin/shared/constants";
import {
  contractPeriodAmount,
  formatContractPeriod,
  renderContractStatus,
} from "@/admin/features/billing";

interface OrganizationCurrentContractsProps {
  organizationId: string;
}

/**
 * «Текущие» контракты: действующие и ещё не начавшиеся. Сервер принимает
 * один статус фильтра за раз, а контрактов у организации единицы, поэтому
 * отбираем оба статуса на клиенте, а не заводим отдельный серверный
 * фильтр под это.
 */
const CURRENT_STATUSES = new Set(["active", "pending"]);

/**
 * Блок «Контракты» на вкладке «Детали» организации, только чтение (спека
 * §5): полный CRUD контрактов теперь отдельный раздел меню, здесь только
 * ссылка туда с уже подставленным фильтром по организации.
 */
export const OrganizationCurrentContracts: FC<
  OrganizationCurrentContractsProps
> = ({ organizationId }) => {
  const { data, loading, error } = useQuery<ContractsListQueryType>(
    ContractsListQuery,
    { variables: { filter: { organizationId } } }
  );

  const allContractsHref = `${PagePaths.billingContracts}?filter=${encodeURIComponent(
    JSON.stringify({ organizationId })
  )}`;

  if (error) return <ErrorMessage error={error} />;

  const contracts = loading
    ? []
    : (data?.contracts.nodes ?? []).filter((contract) =>
        CURRENT_STATUSES.has(contract.status)
      );

  return (
    <Stack spacing={1.5}>
      {loading && <LinearProgress />}

      {!loading &&
        (contracts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Текущих контрактов нет
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Название</TableCell>
                <TableCell>Период</TableCell>
                <TableCell>Валюта</TableCell>
                <TableCell align="right">Сумма за период</TableCell>
                <TableCell>Статус</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contracts.map((contract: ContractsListRow) => (
                <TableRow key={contract.id}>
                  <TableCell>{contract.title}</TableCell>
                  <TableCell>
                    {formatContractPeriod(contract.dateFrom, contract.dateTo)}
                  </TableCell>
                  <TableCell>{contract.currency}</TableCell>
                  <TableCell align="right">
                    {contractPeriodAmount(contract)}
                  </TableCell>
                  <TableCell>{renderContractStatus(contract.status)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ))}

      <Box>
        <MuiLink
          component={RouterLink}
          to={allContractsHref}
          color="secondary"
          underline="hover"
        >
          Все контракты организации
        </MuiLink>
      </Box>
    </Stack>
  );
};
